import React, { useEffect, useRef } from 'react';
import { CustomCalendar } from '../broker/visit/CustomCalendar';
import { CustomTimePicker } from '../broker/visit/CustomTimePicker';
import { ExclamationTriangleIcon } from '../Icons';

interface VisitFormFieldsProps {
  formData: any;
  setFormData: (data: any) => void;
  visitConfig: any;
  minDate: string;
  DAYS_ORDER: string[];
  errors: Record<string, string>;
}

export const VisitFormFields: React.FC<VisitFormFieldsProps> = ({
  formData, setFormData, visitConfig, minDate, DAYS_ORDER, errors
}) => {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  const labelClass = (field?: string) =>
    `text-[10px] font-black uppercase ${field && errors[field] ? 'text-red-500' : 'text-gray-500'}`;

  const inputClass = (field: string) =>
    `w-full px-3.5 py-2.5 border rounded-xl text-sm font-semibold outline-none transition-all ${
      errors[field]
        ? 'border-red-500 bg-red-50 ring-2 ring-red-200 text-red-900'
        : 'border-gray-200 bg-white focus:ring-2 focus:ring-sky-500'
    }`;

  const isSameDay = Boolean(formData.fecha1 && formData.fecha2 && formData.fecha1 === formData.fecha2);

  return (
    <div className="flex-grow overflow-y-auto custom-scrollbar p-6 space-y-5">
      {Object.keys(errors).length > 0 && (
        <div ref={bannerRef} className="p-4 bg-red-50 border-2 border-red-300 rounded-2xl flex items-start gap-3 animate-shake">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1 text-xs font-bold text-red-800">
            <p className="font-black uppercase tracking-wider text-[11px] text-red-900">Corrige los siguientes errores:</p>
            <ul className="list-disc pl-4 space-y-0.5 font-semibold text-[11px]">
              {Object.values(errors).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <p className="text-[10px] font-black text-sky-700 uppercase tracking-widest">1. Datos Personales</p>
        <div>
          <label className={labelClass('nombre_cliente')}>Nombre Completo *</label>
          <input type="text" value={formData.nombre_cliente} onChange={e => setFormData({ ...formData, nombre_cliente: e.target.value })} className={inputClass('nombre_cliente')} placeholder="Ej: Pepito Pérez" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass('email_cliente')}>Correo Electrónico *</label>
            <input type="email" value={formData.email_cliente} onChange={e => setFormData({ ...formData, email_cliente: e.target.value })} className={inputClass('email_cliente')} placeholder="cliente@correo.com" />
          </div>
          <div>
            <label className={labelClass('telefono')}>Teléfono Celular *</label>
            <input type="tel" value={formData.telefono} onChange={e => setFormData({ ...formData, telefono: e.target.value })} className={inputClass('telefono')} placeholder="3001234567" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass('tipoId')}>Tipo Doc *</label>
            <select value={formData.tipoId} onChange={e => setFormData({ ...formData, tipoId: e.target.value })} className={inputClass('tipoId')}>
              <option value="CC">Cédula Ciudadanía</option>
              <option value="CE">Cédula Extranjería</option>
              <option value="NIT">NIT</option>
              <option value="PAS">Pasaporte</option>
            </select>
          </div>
          <div>
            <label className={labelClass('numeroId')}>Número Documento *</label>
            <input type="text" value={formData.numeroId} onChange={e => setFormData({ ...formData, numeroId: e.target.value })} className={inputClass('numeroId')} placeholder="123456789" />
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-3 border-t border-gray-100">
        <p className="text-[10px] font-black text-sky-700 uppercase tracking-widest">2. Dos Fechas Obligatorias de Visita</p>
        {[1, 2].map(i => {
          const fKey = `fecha${i}`;
          const hKey = `hora${i}`;
          const hasGroupErr = Boolean(errors[fKey] || errors[hKey] || isSameDay);
          return (
            <div key={i} className={`grid grid-cols-2 gap-3 p-4 rounded-2xl border transition-all ${hasGroupErr ? 'bg-red-50/60 border-red-300 ring-1 ring-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="col-span-2 flex justify-between items-center">
                <span className={`text-[10px] font-black uppercase ${hasGroupErr ? 'text-red-700' : 'text-gray-600'}`}>
                  Opción {i === 1 ? '1 (Principal) *' : '2 (Alternativa) *'}
                </span>
                {(errors[fKey] || errors[hKey]) && <span className="text-[10px] font-bold text-red-600">Requerido</span>}
              </div>
              <div>
                <label className={labelClass(fKey)}>Fecha {i} *</label>
                <CustomCalendar value={(formData as any)[fKey]} onChange={v => setFormData({ ...formData, [fKey]: v })} minDate={minDate} visitConfig={visitConfig} />
              </div>
              <div>
                <label className={labelClass(hKey)}>Hora {i} *</label>
                <CustomTimePicker value={(formData as any)[hKey]} date={(formData as any)[fKey]} onChange={v => setFormData({ ...formData, [hKey]: v })} visitConfig={visitConfig} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
